import type { ReactElement } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTrigger,
} from '@src/design-system/dialog';
import { Button } from '@src/design-system/button';
import { DeleteConfirmDialog } from '@src/design-system/molecules/delete-confirm-dialog-button';
import { useTiclickTableContext } from '@src/design-system/molecules/ticlick-table/context/ticlick-table-context-hook';
import reminderLogsApi from '@src/features/reminders/services/reminder-logs-api';
import type { TReminderLog } from '@src/shared/types/reminder';
import { showToast } from '@src/shared/utils/helpers/show-toast';
import { RefreshLeftSquare } from 'iconsax-react';
import toast from 'react-hot-toast';

export type ReminderLogResendButtonProps = {
  ids: number[];
  trigger?: ReactElement;
  disabled?: boolean;
};

function getResendCopy(count: number) {
  const isBulk = count > 1;

  return {
    dialogHeader: isBulk ? 'Resend Reminders' : 'Resend Reminder',
    confirmTitle: isBulk
      ? `Are you sure you want to resend ${count} reminders?`
      : 'Are you sure you want to resend this reminder?',
    successMessage: isBulk
      ? `${count} reminders resent successfully.`
      : 'Reminder resent successfully.',
  };
}

function DefaultResendTrigger({ disabled }: { disabled?: boolean }) {
  return (
    <Button
      type='button'
      variant='ghost'
      size='iconSm'
      className='text-primary-1000'
      aria-label='Resend reminder'
      title='Resend'
      disabled={disabled}
    >
      <RefreshLeftSquare
        size={18}
        variant='TwoTone'
      />
    </Button>
  );
}

export function ReminderLogResendButton({
  ids,
  trigger,
  disabled,
}: ReminderLogResendButtonProps) {
  const { table } = useTiclickTableContext<TReminderLog>();
  const isDisabled = disabled ?? ids.length === 0;
  const { mutate: resendLog, isPending } =
    reminderLogsApi.useResendReminderLog();
  const { dialogHeader, confirmTitle, successMessage } = getResendCopy(
    ids.length
  );

  return (
    <Dialog>
      <DialogTrigger disabled={isDisabled}>
        {trigger ?? <DefaultResendTrigger disabled={isDisabled} />}
      </DialogTrigger>
      <DialogContent
        align='center'
        className='max-w-[560px]'
      >
        <DialogHeader className='p-4'>{dialogHeader}</DialogHeader>
        <DeleteConfirmDialog
          icon={
            <span className='bg-primary-100 size-13 flex justify-center items-center rounded-full'>
              <RefreshLeftSquare
                size={25}
                className='text-primary-1000'
              />
            </span>
          }
          title={confirmTitle}
          confirmText='Yes, resend'
          cancelText='No, back'
          isLoading={isPending}
          onConfirm={(closeModal) => {
            if (!ids.length) return;

            resendLog(ids, {
              onSuccess: () => {
                showToast({
                  title: 'Re-Sent Successfully',
                  description: successMessage,
                  variant: 'success',
                });
                table.resetRowSelection();
                closeModal();
              },
              onError: (error) => {
                toast.error(`${error}`);
              },
            });
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
